import {
  Card,
  CardHeader,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Typography
} from '@mui/material';
import {
  DataGrid, gridPageCountSelector,
  gridPageSelector,
  koKR,
  useGridApiContext,
  useGridSelector
} from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../../api/axiosClient';
import CustomChip from '../../components/mui/chip';
import Spinner from '../../components/spinner';
import CustomNoRowsOverlay from '../../utils/CustomNoRowsOverlay';
import InquiryToolbar from './InquiryToolbar';

const TableInquiry = () => {
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [sort, setSort] = useState('desc');
  const [pageSize, setPageSize] = useState(10);
  const [list, setList] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [sortColumn, setSortColumn] = useState('qid');
  const [selected, setSelected] = useState([]);

  const loadInquiry = 
    async (sort, query, column) => {
      try {
        const response = await apiService.get('/inquiry', {
          params: {
            page,
            pageSize,
            query,
            sort,
            column          
          }
        });
        
        setTotal(response.data.size);
        setList(response.data.list);
        setLoading(false);
      } catch (error) {
        console.error(error);
      }
    }

  useEffect(() => {
    loadInquiry(sort, searchValue, sortColumn);
  }, [page, pageSize, reload]);

  const handleSortModel = (newModel) => {
    if (newModel.length) {
      setSort(newModel[0].sort)
      setSortColumn(newModel[0].field)
      loadInquiry(newModel[0].sort, searchValue, newModel[0].field)
    } else {
      setSort('desc');
      setSortColumn('qid');
    }
  }

  const handleSearch = value => {
    setSearchValue(value);
    loadInquiry(sort, value, sortColumn);
  }

  const onPageSizeChange = e => {
    setPageSize(e.target.value);
  }

  function CustomPagination() {
    const apiRef = useGridApiContext();
    const _page = useGridSelector(apiRef, gridPageSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
      <Stack direction="row" spacing={2} sx={{ pr: 4, alignItems: 'center' }}>
        <InputLabel id="pagesize-select">행/페이지</InputLabel>
        <FormControl variant="standard" sx={{ mr: 1, minWidth: 70 }}>
          <Select
            labelId="pagesize-select"
            id="pagesize-select"
            value={pageSize}
            onChange={onPageSizeChange}
            label="pageSize"
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </FormControl>
        <Pagination
          color="primary"
          count={pageCount}
          page={_page + 1}
          onChange={(event, value) => apiRef.current.setPage(value - 1)}
        />
      </Stack>
    );
  }

  const columns = [
    {
      minWidth: 120,
      field: 'qid',
      headerName: '문의 ID',
      renderCell: params => (
        <Link to={`/admin/inquiry/${params.row.qid}`} >
          <CustomChip
            size='small'
            skin='light'
            color='primary'
            label={params.row.qid}
            sx={{ '& .MuiChip-label': { textTransform: 'capitalize' }, padding: 2, cursor: 'pointer' }}
          />
        </Link>
      )
    },
    {
      minWidth: 140,
      field: 'kind',
      headerName: '문의유형',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.kind}
        </Typography>
      )
    },
    {
      minWidth: 200,
      field: 'corpname',
      headerName: '법인명',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.corpname}
        </Typography>
      )
    },
    {
      minWidth: 100,
      field: 'name',      
      headerName: '이름',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.name}
        </Typography>
      )
    },
    {
      minWidth: 200,
      field: 'contact',      
      headerName: '연락처',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.contact}
        </Typography>
      )
    },
    {
      minWidth: 200,
      field: 'email',      
      headerName: '이메일',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.email}
        </Typography>
      )
    },
    {
      minWidth: 400,
      field: 'request',      
      headerName: '문의 내용',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.request}
        </Typography>
      )
    },
    {
      minWidth: 200,
      field: 'uptime',      
      headerName: '변경날짜',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.uptime.toLocaleString('ko-KR')}
        </Typography>
      )
    }
  ]

  return (
    loading ?
    <Spinner/>
    :
    <Card>
      <CardHeader title='빠른문의 내역' />
      <DataGrid
        sx={{
          ...((list?.length || 0) == 0 && { '& .MuiDataGrid-virtualScrollerContent': {height: '200px !important' } })
        }}
        localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
        autoHeight
        getRowId={row => row.qid}
        pagination
        rows={list || []}
        rowCount={total || 0}
        columns={columns}
        pageSize={pageSize}
        sortingMode='server'
        paginationMode='server'
        onSortModelChange={handleSortModel}
        onPageChange={newPage => setPage(newPage)}
        components={{
          Toolbar: InquiryToolbar,
          Pagination: CustomPagination,
          NoRowsOverlay: CustomNoRowsOverlay,
        }}
        componentsProps={{
          toolbar: {
            value: searchValue,
            clearSearch: () => handleSearch(''),
            onChange: event => handleSearch(event.target.value),
            numSelected: selected?.length || 0,
            selected,
            setReload
          }
        }}
        checkboxSelection
        disableSelectionOnClick
        onSelectionModelChange={(ids) => {
          const selectedIDs = new Set(ids);
          const selectedRows = (list || []).filter((row) =>
            selectedIDs.has(row.qid),
          );

          setSelected(selectedRows)
        }}
        experimentalFeatures={{ newEditingApi: true }}
      />
    </Card>
  )
}

export default TableInquiry
